import { ROAD_LABEL_CLASSES } from './roadHierarchy';
import type {
  AddLayerObject,
  ExpressionSpecification,
  FillExtrusionLayerSpecification,
  StyleSpecification,
} from 'maplibre-gl';

import {
  basemapPreset,
  basemapProviderFor,
  DEFAULT_BASEMAP_CUSTOMIZATION_BY_PRESET,
  effectiveBasemapCustomization,
  type BasemapConfiguration,
  type BasemapCustomization,
  type CuratedBasemapPresetId,
  type EffectiveBasemapCustomization,
  type BasemapRuntimeContext,
  type BasemapSemanticGroup,
} from './catalog';
import { createNeutralMapStyle } from '../map/neutralStyle';
import { mixHexColour } from '../styles/colour';
import { applyBasemapLabelTypography } from './labelTypography';
import { BASEMAP_LABEL_GROUPS } from './labelPresentation';
import type { BasemapLabelGroup } from './labelAppearance';

// Provider snapshots are immutable in the adapter/export caches. Keep classifications tied to
// their object identity, never to a global layer ID or an authored appearance setting.
const providerLabelGroups = new WeakMap<StyleSpecification, Map<string, BasemapLabelGroup>>();

export const DROPMAP_3D_BUILDINGS_LAYER_ID = 'dropmap-basemap-buildings-3d';
export const DROPMAP_GREEN_CONTEXT_LAYER_ID = 'dropmap-basemap-green-context';
export const DROPMAP_3D_BUILDINGS_MIN_ZOOM = 14;
export const DROPMAP_DARK_LABEL_TEXT_COLOUR = 'rgba(226, 230, 234, 0.94)';
export const DROPMAP_DARK_LABEL_HALO_COLOUR = 'rgba(18, 22, 27, 0.88)';

export type BasemapStyleFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
type BasemapRuntimeConfiguration = Omit<BasemapConfiguration, 'customizationByPreset'> & {
  readonly customizationByPreset?: BasemapConfiguration['customizationByPreset'];
};

function completeConfiguration(configuration: BasemapRuntimeConfiguration): BasemapConfiguration {
  return {
    ...configuration,
    customizationByPreset:
      configuration.customizationByPreset ?? DEFAULT_BASEMAP_CUSTOMIZATION_BY_PRESET,
  };
}

export function multiplyStyleOpacity(existingValue: unknown, factor: number): unknown {
  if (factor === 1) return existingValue;
  if (existingValue === undefined) return factor;
  if (typeof existingValue === 'number') return existingValue * factor;
  if (Array.isArray(existingValue)) {
    if (containsZoomExpression(existingValue)) {
      if (existingValue[0] === 'interpolate') {
        return existingValue.map((value, index) =>
          index >= 4 && index % 2 === 0 ? multiplyStyleOpacity(value, factor) : value,
        );
      }
      if (existingValue[0] === 'step') {
        return existingValue.map((value, index) =>
          index >= 2 && index % 2 === 0 ? multiplyStyleOpacity(value, factor) : value,
        );
      }
      return existingValue;
    }
    return ['*', existingValue, factor];
  }
  return existingValue;
}

function containsZoomExpression(value: readonly unknown[]): boolean {
  return value.some(
    (part) =>
      (Array.isArray(part) && part.length === 1 && part[0] === 'zoom') ||
      (Array.isArray(part) && containsZoomExpression(part)),
  );
}

function sourceLayer(layer: StyleSpecification['layers'][number]): string | undefined {
  return 'source-layer' in layer && typeof layer['source-layer'] === 'string'
    ? layer['source-layer']
    : undefined;
}

function isTextLayer(layer: StyleSpecification['layers'][number]): boolean {
  return layer.type === 'symbol' && !!layer.layout && 'text-field' in layer.layout;
}

function isIconLayer(layer: StyleSpecification['layers'][number]): boolean {
  return layer.type === 'symbol' && !!layer.layout && 'icon-image' in layer.layout;
}

function filterText(layer: StyleSpecification['layers'][number]): string {
  return JSON.stringify('filter' in layer ? layer.filter : null);
}

function filterMentions(layer: StyleSpecification['layers'][number], values: readonly string[]) {
  const serialized = filterText(layer);
  return values.some((value) => serialized.includes(`"${value}"`));
}

function hasLayerFilter(layer: StyleSpecification['layers'][number]): boolean {
  return 'filter' in layer && layer.filter !== undefined;
}

const MANAGED_GREEN_CLASSES = [
  'park',
  'garden',
  'recreation_ground',
  'golf_course',
  'pitch',
  'playground',
  'cemetery',
  'flowerbed',
] as const;
const NATURAL_GREEN_CLASSES = [
  'wood',
  'forest',
  'scrub',
  'heath',
  'shrubbery',
  'grass',
  'grassland',
  'meadow',
  'wetland',
] as const;
const LOCALITY_PLACE_CLASSES = [
  'borough',
  'hamlet',
  'isolated_dwelling',
  'neighbourhood',
  'quarter',
  'suburb',
  'village',
] as const;

function isTransitLineLayer(layer: StyleSpecification['layers'][number]): boolean {
  return (
    sourceLayer(layer) === 'transportation' &&
    layer.type !== 'symbol' &&
    (/(?:rail|transit|subway|tram)/i.test(layer.id) ||
      filterMentions(layer, ['rail', 'transit', 'subway', 'tram']))
  );
}

function expressionGetsProperty(value: unknown, property: string): boolean {
  return (
    Array.isArray(value) &&
    ((value[0] === 'get' && value[1] === property) ||
      value.some((part) => expressionGetsProperty(part, property)))
  );
}

function filterIncludesPropertyValues(
  value: unknown,
  property: string,
  targets: readonly string[],
): boolean {
  if (!Array.isArray(value)) return false;
  if (value[0] === '!') return false;
  if (
    value[0] === '==' &&
    Array.isArray(value[1]) &&
    value[1][0] === 'get' &&
    value[1][1] === property &&
    typeof value[2] === 'string'
  ) {
    return targets.includes(value[2]);
  }
  if (
    value[0] === 'match' &&
    Array.isArray(value[1]) &&
    value[1][0] === 'get' &&
    value[1][1] === property &&
    value[value.length - 2] === true &&
    value[value.length - 1] === false
  ) {
    const candidates = Array.isArray(value[2]) ? value[2] : [value[2]];
    return candidates.some(
      (candidate) => typeof candidate === 'string' && targets.includes(candidate),
    );
  }
  return value.some((part) => filterIncludesPropertyValues(part, property, targets));
}

function filterExcludesPropertyValues(
  value: unknown,
  property: string,
  targets: readonly string[],
): boolean {
  if (!Array.isArray(value)) return false;
  if (
    value[0] === 'match' &&
    Array.isArray(value[1]) &&
    value[1][0] === 'get' &&
    value[1][1] === property &&
    value[value.length - 2] === false &&
    value[value.length - 1] === true
  ) {
    const candidates = Array.isArray(value[2]) ? value[2] : [value[2]];
    return targets.every((target) => candidates.includes(target));
  }
  return value.some((part) => filterExcludesPropertyValues(part, property, targets));
}

function isTransitLabelLayer(layer: StyleSpecification['layers'][number]): boolean {
  return (
    sourceLayer(layer) === 'poi' &&
    isTextLayer(layer) &&
    filterIncludesPropertyValues('filter' in layer ? layer.filter : undefined, 'class', [
      'airport',
      'bus',
      'ferry',
      'rail',
      'railway',
      'subway',
      'tram',
    ])
  );
}

function isRoadLayer(layer: StyleSpecification['layers'][number]): boolean {
  return (
    sourceLayer(layer) === 'transportation' &&
    layer.type !== 'symbol' &&
    !isTransitLineLayer(layer) &&
    !filterMentions(layer, ['pier'])
  );
}

function isRouteShieldLayer(layer: StyleSpecification['layers'][number]): boolean {
  const textField = isTextLayer(layer)
    ? (layer as { readonly layout?: Readonly<Record<string, unknown>> }).layout?.['text-field']
    : undefined;
  return (
    sourceLayer(layer) === 'transportation_name' &&
    isTextLayer(layer) &&
    expressionGetsProperty(textField, 'ref')
  );
}

function isRoadNameLayer(layer: StyleSpecification['layers'][number]): boolean {
  return (
    sourceLayer(layer) === 'transportation_name' && isTextLayer(layer) && !isRouteShieldLayer(layer)
  );
}

function isNaturalWaterLabelLayer(layer: StyleSpecification['layers'][number]): boolean {
  return (
    isTextLayer(layer) && (sourceLayer(layer) === 'water_name' || sourceLayer(layer) === 'waterway')
  );
}

function placeLabelGroup(
  layer: StyleSpecification['layers'][number],
): 'citiesTowns' | 'suburbsLocalities' | 'otherLabels' {
  const group = (layer.metadata as Record<string, unknown> | undefined)?.['placeio:labelGroup'];
  if (group === 'citiesTowns' || group === 'suburbsLocalities' || group === 'otherLabels')
    return group;
  const filter = 'filter' in layer ? layer.filter : undefined;
  if (filterIncludesPropertyValues(filter, 'class', ['city', 'town'])) return 'citiesTowns';
  if (filterIncludesPropertyValues(filter, 'class', LOCALITY_PLACE_CLASSES)) {
    return 'suburbsLocalities';
  }
  return 'otherLabels';
}

function splitMixedPlaceLabelLayers(style: StyleSpecification): void {
  style.layers = style.layers.flatMap((layer) => {
    const filter = 'filter' in layer ? layer.filter : undefined;
    if (
      sourceLayer(layer) !== 'place' ||
      !isTextLayer(layer) ||
      !filterExcludesPropertyValues(filter, 'class', ['city', 'town', 'village'])
    ) {
      return [layer];
    }
    const localities = ['match', ['get', 'class'], [...LOCALITY_PLACE_CLASSES], true, false];
    const localitiesLayer = {
      ...layer,
      id: `dropmap-place-localities-${layer.id}`,
      filter: ['all', filter ?? true, localities],
    } as unknown as StyleSpecification['layers'][number];
    const otherLayer = {
      ...layer,
      id: `dropmap-place-other-${layer.id}`,
      filter: ['all', filter ?? true, ['!', localities]],
    } as unknown as StyleSpecification['layers'][number];
    return [localitiesLayer, otherLayer];
  });
}

function isParksGreenSpaceLayer(layer: StyleSpecification['layers'][number]): boolean {
  const layerSource = sourceLayer(layer);
  return (
    layer.type !== 'symbol' &&
    (layerSource === 'park' ||
      (layerSource === 'landuse' &&
        (!hasLayerFilter(layer) || filterMentions(layer, MANAGED_GREEN_CLASSES))) ||
      (layerSource === 'landcover' &&
        (!hasLayerFilter(layer) || filterMentions(layer, ['grass', ...MANAGED_GREEN_CLASSES]))))
  );
}

function isNaturalVegetationLayer(layer: StyleSpecification['layers'][number]): boolean {
  return (
    layer.type !== 'symbol' &&
    sourceLayer(layer) === 'landcover' &&
    (!hasLayerFilter(layer) || filterMentions(layer, NATURAL_GREEN_CLASSES))
  );
}

function layerMatchesSemanticGroup(
  layer: StyleSpecification['layers'][number],
  configuration: BasemapConfiguration,
  group: BasemapSemanticGroup,
): boolean {
  if (isTextLayer(layer) && BASEMAP_LABEL_GROUPS.some((label) => label === group)) {
    const classified = (layer.metadata as Record<string, unknown> | undefined)?.[
      'placeio:labelGroup'
    ];
    if (BASEMAP_LABEL_GROUPS.some((label) => label === classified)) return classified === group;
  }
  const layerSource = sourceLayer(layer);
  if (group === 'roadNames') return isRoadNameLayer(layer);
  if (group === 'routeShields') return isRouteShieldLayer(layer);
  if (group === 'transitLabels') return isTransitLabelLayer(layer);
  if (group === 'naturalWaterLabels') return isNaturalWaterLabelLayer(layer);
  if (group === 'citiesTowns') {
    return layerSource === 'place' && isTextLayer(layer) && placeLabelGroup(layer) === group;
  }
  if (group === 'suburbsLocalities') {
    return layerSource === 'place' && isTextLayer(layer) && placeLabelGroup(layer) === group;
  }
  if (group === 'otherLabels') {
    return (
      isTextLayer(layer) &&
      !isRoadNameLayer(layer) &&
      !isRouteShieldLayer(layer) &&
      !isTransitLabelLayer(layer) &&
      !isNaturalWaterLabelLayer(layer) &&
      layerSource !== 'poi' &&
      (layerSource !== 'place' || placeLabelGroup(layer) === group)
    );
  }
  if (group === 'pointsOfInterest') {
    return layerSource === 'poi' && isTextLayer(layer) && !isTransitLabelLayer(layer);
  }
  if (group === 'transitLines') return isTransitLineLayer(layer);
  if (group === 'roads') return isRoadLayer(layer);
  if (group === 'parksGreenSpace') return isParksGreenSpaceLayer(layer);
  if (group === 'naturalVegetation') return isNaturalVegetationLayer(layer);
  if (group === 'water') {
    return layer.type !== 'symbol' && (layerSource === 'water' || layerSource === 'waterway');
  }
  if (group === 'buildings') return layer.type !== 'symbol' && layerSource === 'building';
  return false;
}

function setLayerOpacity(
  layer: StyleSpecification['layers'][number],
  property: string,
  factor: number,
): void {
  const mutableLayer = layer as unknown as { paint?: Record<string, unknown> };
  const paint = mutableLayer.paint ?? {};
  const multiplied = multiplyStyleOpacity(paint[property], factor);
  if (multiplied === undefined) return;
  mutableLayer.paint = { ...paint, [property]: multiplied };
}

function applyContextOpacity(layer: StyleSpecification['layers'][number], factor: number): void {
  switch (layer.type) {
    case 'background':
      setLayerOpacity(layer, 'background-opacity', factor);
      break;
    case 'fill':
      setLayerOpacity(layer, 'fill-opacity', factor);
      break;
    case 'line':
      setLayerOpacity(layer, 'line-opacity', factor);
      break;
    case 'circle':
      setLayerOpacity(layer, 'circle-opacity', factor);
      setLayerOpacity(layer, 'circle-stroke-opacity', factor);
      break;
    case 'fill-extrusion':
      setLayerOpacity(layer, 'fill-extrusion-opacity', factor);
      break;
    case 'raster':
      setLayerOpacity(layer, 'raster-opacity', factor);
      break;
  }
}

function layerColourProperty(
  layer: StyleSpecification['layers'][number],
): 'fill-color' | 'line-color' | 'circle-color' | null {
  return layer.type === 'fill'
    ? 'fill-color'
    : layer.type === 'line'
      ? 'line-color'
      : layer.type === 'circle'
        ? 'circle-color'
        : null;
}

function setLayerColour(layer: StyleSpecification['layers'][number], colour: string): void {
  const property = layerColourProperty(layer);
  if (property) {
    const mutableLayer = layer as unknown as { paint?: Record<string, unknown> };
    mutableLayer.paint = { ...mutableLayer.paint, [property]: colour };
  }
}

function hideLayer(layer: StyleSpecification['layers'][number]): void {
  const mutableLayer = layer as unknown as { layout?: Record<string, unknown> };
  mutableLayer.layout = { ...mutableLayer.layout, visibility: 'none' };
}

function showLayer(layer: StyleSpecification['layers'][number]): void {
  const mutableLayer = layer as unknown as { layout?: Record<string, unknown> };
  mutableLayer.layout = { ...mutableLayer.layout, visibility: 'visible' };
}

function roadColours(
  customization: EffectiveBasemapCustomization,
  casing: boolean,
  configuration: BasemapConfiguration,
): readonly [string, string, string, string] {
  const adjust = (colour: string) =>
    casing
      ? mixHexColour(
          colour,
          basemapPreset(configuration.presetId).theme === 'dark' ? '#000000' : '#ffffff',
          0.24,
        )
      : colour;
  return [
    adjust(customization.colours.motorwaysHighways),
    adjust(customization.colours.arterialRoads),
    adjust(customization.colours.collectorRoads),
    adjust(customization.colours.localRoads),
  ];
}

function setRoadLayerColours(
  layer: StyleSpecification['layers'][number],
  customization: EffectiveBasemapCustomization,
  configuration: BasemapConfiguration,
): void {
  const property =
    layer.type === 'fill'
      ? 'fill-color'
      : layer.type === 'line'
        ? 'line-color'
        : layer.type === 'circle'
          ? 'circle-color'
          : null;
  if (!property) return;
  const [motorwaysHighways, arterial, collector, local] = roadColours(
    customization,
    /(?:casing|case|outline)/i.test(layer.id),
    configuration,
  );
  const mutableLayer = layer as unknown as { paint?: Record<string, unknown> };
  mutableLayer.paint = {
    ...mutableLayer.paint,
    [property]: [
      'match',
      ['get', 'class'],
      [...ROAD_LABEL_CLASSES.motorwaysHighways],
      motorwaysHighways,
      [...ROAD_LABEL_CLASSES.arterialRoads],
      arterial,
      ROAD_LABEL_CLASSES.collectorRoads[0],
      collector,
      [...ROAD_LABEL_CLASSES.localRoads],
      local,
      local,
    ],
  };
}

function matchProperty(
  property: 'class' | 'subclass',
  values: readonly string[],
): ExpressionSpecification {
  return ['match', ['get', property], [...values], true, false] as ExpressionSpecification;
}

function greenPredicates(layer: StyleSpecification['layers'][number]): {
  readonly managed: boolean | unknown[];
  readonly natural: boolean | unknown[];
} | null {
  const layerSource = sourceLayer(layer);
  if (layerSource === 'park') return { managed: true, natural: false };
  if (layerSource === 'landuse') {
    return { managed: matchProperty('class', MANAGED_GREEN_CLASSES), natural: false };
  }
  if (layerSource === 'landcover') {
    const managed = matchProperty('subclass', MANAGED_GREEN_CLASSES);
    return {
      managed,
      natural: [
        'all',
        ['!', managed],
        [
          'any',
          matchProperty('class', ['wood', 'forest', 'grass', 'wetland']),
          matchProperty('subclass', NATURAL_GREEN_CLASSES),
        ],
      ],
    };
  }
  return null;
}

function mapTopLevelZoomOutputs(value: unknown, transform: (output: unknown) => unknown): unknown {
  if (!Array.isArray(value)) return transform(value);
  if (value[0] === 'interpolate' && Array.isArray(value[2]) && value[2][0] === 'zoom') {
    return value.map((item, index) => (index >= 4 && index % 2 === 0 ? transform(item) : item));
  }
  if (value[0] === 'step' && Array.isArray(value[1]) && value[1][0] === 'zoom') {
    return value.map((item, index) => (index >= 2 && index % 2 === 0 ? transform(item) : item));
  }
  return transform(value);
}

function regionalRoadOpacity(value: unknown): unknown {
  const strength = (zoom: number) =>
    zoom <= 8
      ? 0.55
      : zoom >= 15
        ? 1
        : zoom < 12
          ? 0.55 + ((zoom - 8) * 0.25) / 4
          : 0.8 + ((zoom - 12) * 0.2) / 3;
  if (Array.isArray(value) && containsZoomExpression(value)) {
    if (value[0] !== 'interpolate' && value[0] !== 'step') return value;
    const firstOutput = value[0] === 'interpolate' ? 4 : 2;
    return value.map((part, index) =>
      index >= firstOutput && index % 2 === 0
        ? multiplyStyleOpacity(part, strength(index === 2 ? 0 : Number(value[index - 1])))
        : part,
    );
  }
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    8,
    multiplyStyleOpacity(value, 0.55),
    12,
    multiplyStyleOpacity(value, 0.8),
    15,
    value,
  ];
}

function applyGreenCustomization(
  layer: StyleSpecification['layers'][number],
  customization: EffectiveBasemapCustomization,
): boolean {
  const predicates = greenPredicates(layer);
  const colourProperty = layerColourProperty(layer);
  if (!predicates || !colourProperty) return false;
  const opacityProperty =
    layer.type === 'fill'
      ? 'fill-opacity'
      : layer.type === 'line'
        ? 'line-opacity'
        : 'circle-opacity';
  const mutableLayer = layer as unknown as { paint?: Record<string, unknown> };
  const paint = mutableLayer.paint ?? {};
  const originalColour = paint[colourProperty] ?? 'rgba(0, 0, 0, 0)';
  const originalOpacity = paint[opacityProperty] ?? 1;
  const colourForFeatures = (fallback: unknown) => [
    'case',
    predicates.managed,
    customization.colours.parksGreenSpace,
    predicates.natural,
    customization.colours.naturalVegetation,
    fallback,
  ];
  const opacityForFeatures = (fallback: unknown) => [
    'case',
    predicates.managed,
    customization.visibility.parksGreenSpace ? fallback : 0,
    predicates.natural,
    customization.visibility.naturalVegetation ? fallback : 0,
    fallback,
  ];
  mutableLayer.paint = {
    ...paint,
    [colourProperty]: mapTopLevelZoomOutputs(originalColour, colourForFeatures),
    [opacityProperty]: mapTopLevelZoomOutputs(originalOpacity, opacityForFeatures),
  };
  return true;
}

function addMissingGreenContextLayer(style: StyleSpecification): void {
  const hasGrassFill = style.layers.some(
    (layer) =>
      layer.type === 'fill' &&
      sourceLayer(layer) === 'landcover' &&
      (!hasLayerFilter(layer) || filterMentions(layer, ['grass'])),
  );
  if (hasGrassFill) return;
  const source = style.layers.find((layer) => sourceLayer(layer) === 'landcover');
  if (!source || !('source' in source) || typeof source.source !== 'string') return;
  const insertionIndex = style.layers.reduce(
    (last, layer, index) =>
      ['park', 'landuse', 'landcover'].includes(sourceLayer(layer) ?? '') ? index + 1 : last,
    0,
  );
  style.layers.splice(insertionIndex, 0, {
    id: DROPMAP_GREEN_CONTEXT_LAYER_ID,
    type: 'fill',
    source: source.source,
    'source-layer': 'landcover',
    filter: [
      'any',
      matchProperty('subclass', [...MANAGED_GREEN_CLASSES, ...NATURAL_GREEN_CLASSES]),
      matchProperty('class', ['wood', 'forest', 'grass', 'wetland']),
    ] as ExpressionSpecification,
    paint: { 'fill-color': 'rgba(0, 0, 0, 0)' },
  });
}

function applySemanticCustomization(
  layers: readonly StyleSpecification['layers'][number][],
  configuration: BasemapConfiguration,
): void {
  if (configuration.presetId === 'none') return;
  const presetId = configuration.presetId as CuratedBasemapPresetId;
  const overrides: BasemapCustomization = configuration.customizationByPreset[presetId];
  const customization = effectiveBasemapCustomization(configuration, presetId);
  for (const layer of layers) {
    if (layer.id === 'dropmap-background') continue;
    if (layer.type !== 'symbol') applyContextOpacity(layer, customization.contextOpacity);

    if (isTextLayer(layer)) {
      setLayerOpacity(layer, 'text-opacity', customization.labelsOpacity);
      if (isIconLayer(layer)) setLayerOpacity(layer, 'icon-opacity', customization.labelsOpacity);
    }

    const greenCustomized = applyGreenCustomization(layer, customization);

    if (sourceLayer(layer) === 'boundary' && layer.type === 'line') {
      const boundaryGroups = [
        'countryBoundaries',
        'regionalBoundaries',
        'localBoundaries',
      ] as const;
      if (boundaryGroups.every((group) => !customization.visibility[group])) hideLayer(layer);
      else if (boundaryGroups.some((group) => overrides.visibility[group] === true))
        showLayer(layer);
      // Keep provider filters (including maritime/disputed rules) and zoom/paint hierarchy.
      // The numeric schema property selects a tier within mixed provider layers.
      const level = ['coalesce', ['get', 'admin_level'], 2];
      const visible = [
        'case',
        ['<=', level, 2],
        customization.visibility.countryBoundaries,
        ['<=', level, 4],
        customization.visibility.regionalBoundaries,
        customization.visibility.localBoundaries,
      ];
      const opacity = layer.paint?.['line-opacity'] ?? 1;
      layer.paint = {
        ...layer.paint,
        'line-opacity': mapTopLevelZoomOutputs(opacity, (value) => [
          'case',
          visible,
          value,
          0,
        ]) as ExpressionSpecification,
      };
    }

    for (const group of [
      'citiesTowns',
      'suburbsLocalities',
      'roadNames',
      'routeShields',
      'transitLabels',
      'naturalWaterLabels',
      'otherLabels',
      'roads',
      'water',
      'buildings',
      'pointsOfInterest',
      'transitLines',
    ] as const) {
      if (!layerMatchesSemanticGroup(layer, configuration, group)) continue;
      if (!customization.visibility[group]) hideLayer(layer);
      else if (overrides.visibility[group] === true) showLayer(layer);
    }

    if (isRoadLayer(layer)) {
      setRoadLayerColours(layer, customization, configuration);
      continue;
    }

    for (const group of ['water'] as const) {
      if (layerMatchesSemanticGroup(layer, configuration, group)) {
        setLayerColour(layer, customization.colours[group]);
      }
    }
    if (greenCustomized) continue;
  }
}

/** Family-wide scale hierarchy, applied before authored overrides in every render path. */
function refineFamilyHierarchy(style: StyleSpecification, family: CuratedBasemapPresetId): void {
  const dark = family === 'dropmap-dark';
  const minimumZoom = (layer: StyleSpecification['layers'][number], zoom: number) => {
    layer.minzoom = Math.max(layer.minzoom ?? 0, zoom);
  };
  for (const layer of style.layers) {
    const source = sourceLayer(layer);
    if (source === 'building' && layer.type === 'fill') {
      minimumZoom(layer, 14);
      // Liberty's 2D layer originally ended where its removed provider extrusion began.
      if (layer.maxzoom === 14) delete layer.maxzoom;
      if (family === 'dropmap-light') setLayerOpacity(layer, 'fill-opacity', 0.4);
      if (dark) {
        layer.paint = { ...layer.paint, 'fill-color': '#30363c', 'fill-outline-color': '#343b42' };
        setLayerOpacity(layer, 'fill-opacity', 0.55);
      }
    }
    if (isRoadLayer(layer)) {
      const filter = 'filter' in layer ? layer.filter : undefined;
      const local = filterIncludesPropertyValues(filter, 'class', [
        'minor',
        'residential',
        'service',
        'track',
        'street',
        'street_limited',
        'living_street',
        'path',
        'pedestrian',
      ]);
      const major = filterIncludesPropertyValues(filter, 'class', [
        'motorway',
        'trunk',
        'primary',
        'secondary',
        'tertiary',
      ]);
      if (local && !major) minimumZoom(layer, 12);
      if (family === 'dropmap-streets' && layer.type === 'line' && major) {
        // Preserve all widths/dashes/bridge structure and valid top-level zoom expressions.
        const paint = layer.paint ?? {};
        const opacity = paint['line-opacity'] ?? 1;
        layer.paint = {
          ...paint,
          'line-opacity': regionalRoadOpacity(opacity) as ExpressionSpecification,
        };
      }
    }
    if (isRouteShieldLayer(layer)) minimumZoom(layer, 12);
    if (isTransitLabelLayer(layer)) minimumZoom(layer, 14);
    else if (source === 'poi' && layer.type === 'symbol') minimumZoom(layer, 15);
    if (
      source === 'place' &&
      isTextLayer(layer) &&
      placeLabelGroup(layer) === 'suburbsLocalities'
    ) {
      minimumZoom(layer, 11);
    }
    if (source === 'boundary' && layer.type === 'line')
      setLayerOpacity(layer, 'line-opacity', 0.65);
    if (dark && layer.type === 'background')
      layer.paint = { ...layer.paint, 'background-color': '#24282d' };
    if (dark && source === 'landuse' && filterMentions(layer, ['residential'])) {
      setLayerColour(layer, '#292e33');
    }
    if (dark && source === 'aeroway') setLayerColour(layer, '#30363b');
  }
}

export function firstProviderLabelLayerId(
  style: Pick<StyleSpecification, 'layers'>,
): string | undefined {
  return style.layers.find(
    (layer) =>
      layer.type === 'symbol' &&
      !!layer.layout &&
      ('text-field' in layer.layout || 'icon-image' in layer.layout),
  )?.id;
}

function openFreeMapBuildingSource(style: StyleSpecification): string | undefined {
  const buildingLayer = style.layers.find(
    (layer) => 'source-layer' in layer && layer['source-layer'] === 'building',
  );
  if (buildingLayer && 'source' in buildingLayer && typeof buildingLayer.source === 'string') {
    return buildingLayer.source;
  }
  return Object.entries(style.sources).find(([, source]) => source.type === 'vector')?.[0];
}

export function create3dBuildingsLayer(
  style: StyleSpecification,
  configuration: BasemapRuntimeConfiguration,
): FillExtrusionLayerSpecification | undefined {
  if (!configuration.buildings3d || configuration.presetId === 'none') return undefined;
  const provider = basemapProviderFor(configuration.presetId);
  if (!provider.supports3dBuildings) return undefined;
  const source = openFreeMapBuildingSource(style);
  if (!source) return undefined;
  const preset = basemapPreset(configuration.presetId);
  return {
    id: DROPMAP_3D_BUILDINGS_LAYER_ID,
    type: 'fill-extrusion',
    source,
    'source-layer': 'building',
    minzoom: DROPMAP_3D_BUILDINGS_MIN_ZOOM,
    filter: ['!=', ['get', 'hide_3d'], true],
    paint: {
      'fill-extrusion-color': preset.theme === 'dark' ? '#59616d' : '#b8bec5',
      'fill-extrusion-height': ['get', 'render_height'],
      'fill-extrusion-base': ['get', 'render_min_height'],
      'fill-extrusion-opacity': preset.theme === 'dark' ? 0.72 : 0.64,
      'fill-extrusion-vertical-gradient': true,
    },
  };
}

export function composeBasemapStyle(
  providerStyle: StyleSpecification,
  inputConfiguration: BasemapRuntimeConfiguration,
  backgroundColour = '#ffffff',
): StyleSpecification {
  const configuration = completeConfiguration(inputConfiguration);
  const style = structuredClone(providerStyle);
  splitMixedPlaceLabelLayers(style);
  addMissingGreenContextLayer(style);
  if (configuration.presetId !== 'none') {
    for (const layer of [...style.layers]) {
      if (layer.type !== 'line' || sourceLayer(layer) !== 'boundary') continue;
      const filter = JSON.stringify(layer.filter ?? null);
      if (
        !filter.includes(JSON.stringify(['>=', ['get', 'admin_level'], 3])) ||
        !filter.includes(JSON.stringify(['<=', ['get', 'admin_level'], 6]))
      )
        continue;
      // Provider combines levels 3–6. Major regional context is useful earlier than
      // dense local detail; intersect rather than replace its political filters.
      const originalFilter = layer.filter!;
      style.layers.splice(style.layers.indexOf(layer), 0, {
        ...structuredClone(layer),
        id: `${layer.id}-placeio-regional`,
        minzoom: 5,
        filter: [
          'all',
          originalFilter,
          ['<=', ['get', 'admin_level'], 4],
        ] as ExpressionSpecification,
      });
      layer.filter = [
        'all',
        originalFilter,
        ['>=', ['get', 'admin_level'], 5],
      ] as ExpressionSpecification;
    }
    // Dark's provider styles only level 4. Reuse that quiet appearance for the same
    // level 5–6 local detail supported by Light/Streets, hidden by default.
    if (configuration.presetId === 'dropmap-dark') {
      const regional = style.layers.find(
        (layer) =>
          layer.type === 'line' &&
          sourceLayer(layer) === 'boundary' &&
          JSON.stringify(layer.filter) === JSON.stringify(['==', ['get', 'admin_level'], 4]),
      );
      if (regional?.type === 'line') {
        style.layers.splice(style.layers.indexOf(regional), 0, {
          ...structuredClone(regional),
          id: 'dropmap-basemap-local-boundaries',
          minzoom: 8,
          filter: ['all', ['>=', ['get', 'admin_level'], 5], ['<=', ['get', 'admin_level'], 6]],
        });
      }
    }
    refineFamilyHierarchy(style, configuration.presetId);
  }
  const providerLayers = style.layers.filter((layer) => layer.id !== 'dropmap-background');
  const contextLayers = providerLayers.filter(
    (layer) => layer.type !== 'symbol' && layer.type !== 'fill-extrusion',
  );
  const labelLayers = providerLayers.filter((layer) => layer.type === 'symbol');
  style.layers = [
    createNeutralMapStyle(backgroundColour).layers[0]!,
    ...contextLayers,
    ...labelLayers,
  ];
  if (configuration.presetId === 'dropmap-dark') {
    for (const layer of style.layers) {
      if (
        layer.type !== 'symbol' ||
        !layer.layout ||
        !('text-field' in layer.layout) ||
        ('icon-image' in layer.layout &&
          (!('source-layer' in layer) || layer['source-layer'] !== 'place'))
      ) {
        continue;
      }
      layer.paint = {
        ...layer.paint,
        'text-color': DROPMAP_DARK_LABEL_TEXT_COLOUR,
        'text-opacity': 0.92,
        'text-halo-color': DROPMAP_DARK_LABEL_HALO_COLOUR,
        'text-halo-width': 1.2,
        'text-halo-blur': 0.25,
      };
    }
  }
  const buildings = create3dBuildingsLayer(style, configuration);
  if (buildings) style.layers.splice(contextLayers.length + 1, 0, buildings);
  const groups = providerLabelGroups.get(providerStyle) ?? new Map<string, BasemapLabelGroup>();
  for (const layer of style.layers) {
    if (isTextLayer(layer)) {
      const group =
        groups.get(layer.id) ??
        BASEMAP_LABEL_GROUPS.find((group) =>
          layerMatchesSemanticGroup(layer, configuration, group),
        ) ??
        'otherLabels';
      groups.set(layer.id, group);
      layer.metadata = {
        ...(layer.metadata as object),
        'placeio:labelGroup': group,
      };
    }
  }
  providerLabelGroups.set(providerStyle, groups);
  applyBasemapLabelTypography(style, configuration);
  applySemanticCustomization(style.layers, configuration);
  return style;
}

export function semanticBasemapLayerIds(
  style: Pick<StyleSpecification, 'layers'>,
  configuration: BasemapRuntimeConfiguration,
  group: BasemapSemanticGroup,
): readonly string[] {
  const complete = completeConfiguration(configuration);
  return style.layers.flatMap((layer) =>
    layerMatchesSemanticGroup(layer, complete, group) ? [layer.id] : [],
  );
}

export async function resolveMapLibreBasemapStyle(
  inputConfiguration: BasemapRuntimeConfiguration,
  backgroundColour: string,
  signal?: AbortSignal,
  fetchStyle: BasemapStyleFetch = fetch,
  runtime: BasemapRuntimeContext = {},
): Promise<StyleSpecification> {
  const configuration = completeConfiguration(inputConfiguration);
  const providerStyle = await fetchMapLibreProviderStyle(
    configuration,
    signal,
    fetchStyle,
    runtime,
  );
  return providerStyle
    ? composeBasemapStyle(providerStyle, configuration, backgroundColour)
    : createNeutralMapStyle(backgroundColour);
}

export async function fetchMapLibreProviderStyle(
  configuration: BasemapRuntimeConfiguration,
  signal?: AbortSignal,
  fetchStyle: BasemapStyleFetch = fetch,
  runtime: BasemapRuntimeContext = {},
): Promise<StyleSpecification | undefined> {
  const preset = basemapPreset(configuration.presetId);
  const provider = basemapProviderFor(configuration.presetId);
  if (preset.providerId === 'none') return undefined;
  if (!preset.providerStyleId || !provider.resolveStyleUrl) {
    throw new Error(`Basemap preset ${preset.id} has no runtime style resolver.`);
  }
  const response = await fetchStyle(provider.resolveStyleUrl(preset.providerStyleId, runtime), {
    ...(signal ? { signal } : {}),
    credentials: 'omit',
  });
  if (!response.ok) throw new Error(`Basemap style request failed with HTTP ${response.status}.`);
  const style = (await response.json()) as unknown;
  if (
    typeof style !== 'object' ||
    style === null ||
    (style as { version?: unknown }).version !== 8 ||
    !Array.isArray((style as { layers?: unknown }).layers) ||
    typeof (style as { sources?: unknown }).sources !== 'object'
  ) {
    throw new Error('The basemap provider returned an invalid MapLibre style.');
  }
  return style as StyleSpecification;
}

export function splitDropMapLayersForProviderLabels(layers: readonly AddLayerObject[]): {
  readonly focus: readonly AddLayerObject[];
  readonly geometry: readonly AddLayerObject[];
  readonly labels: readonly AddLayerObject[];
  readonly dataFocus: readonly AddLayerObject[];
  readonly annotations: readonly AddLayerObject[];
} {
  return {
    focus: layers.filter((layer) => layer.id === 'dropmap-annotation-focus-basemap'),
    geometry: layers.filter(
      (layer) =>
        layer.id !== 'dropmap-annotation-focus-basemap' &&
        layer.id !== 'dropmap-annotation-focus-data' &&
        !layer.id.startsWith('dropmap-annotation-') &&
        !/-label-(?:point|line|polygon)$/.test(layer.id),
    ),
    labels: layers.filter((layer) => /-label-(?:point|line|polygon)$/.test(layer.id)),
    dataFocus: layers.filter((layer) => layer.id === 'dropmap-annotation-focus-data'),
    annotations: layers.filter(
      (layer) =>
        layer.id.startsWith('dropmap-annotation-') &&
        layer.id !== 'dropmap-annotation-focus-basemap' &&
        layer.id !== 'dropmap-annotation-focus-data',
    ),
  };
}
